import React, { useMemo } from 'react';
import './FilterStyles.css';

const SpendPerPersonFilter = ({ filter, onUpdate, onRemove, originsData }) => {
  // Get all unique spend categories from origins data
  const categories = useMemo(() => {
    if (!originsData || !originsData.origins) {
      return [];
    }

    const categorySet = new Set();
    originsData.origins.forEach(origin => {
      if (origin.spend_per_pers_by_category) {
        let spendData;
        try {
          spendData = typeof origin.spend_per_pers_by_category === 'string'
            ? JSON.parse(origin.spend_per_pers_by_category)
            : origin.spend_per_pers_by_category;
        } catch (e) {
          return;
        }
        Object.keys(spendData).forEach(cat => categorySet.add(cat));
      }
    });

    return Array.from(categorySet).sort();
  }, [originsData]);

  // Calculate min/max spend for the selected category
  const getSpendRange = (category) => {
    if (!originsData || !originsData.origins || !category) {
      return { min: 0, max: 1000 };
    }

    const spendValues = [];
    originsData.origins.forEach(origin => {
      if (origin.spend_per_pers_by_category) {
        let spendData;
        try {
          spendData = typeof origin.spend_per_pers_by_category === 'string'
            ? JSON.parse(origin.spend_per_pers_by_category)
            : origin.spend_per_pers_by_category;
        } catch (e) {
          return;
        }

        if (spendData[category] !== undefined && spendData[category] > 0) {
          spendValues.push(spendData[category]);
        }
      }
    });

    if (spendValues.length === 0) {
      return { min: 0, max: 1000 };
    }

    return {
      min: Math.floor(Math.min(...spendValues)),
      max: Math.ceil(Math.max(...spendValues))
    };
  };

  const { selectedCategory, minSpend, maxSpend } = filter.values;
  const range = selectedCategory ? getSpendRange(selectedCategory) : { min: 0, max: 1000 };

  // Initialize min/max spend if not set
  const currentMinSpend = minSpend !== undefined ? minSpend : range.min;
  const currentMaxSpend = maxSpend !== undefined ? maxSpend : range.max;

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    const newRange = getSpendRange(newCategory);

    onUpdate({
      ...filter,
      values: {
        ...filter.values,
        selectedCategory: newCategory,
        minSpend: newRange.min,
        maxSpend: newRange.max
      }
    });
  };

  const handleMinChange = (e) => {
    const newMin = parseFloat(e.target.value);
    onUpdate({
      ...filter,
      values: {
        ...filter.values,
        minSpend: newMin,
        maxSpend: Math.max(newMin, currentMaxSpend)
      }
    });
  };

  const handleMaxChange = (e) => {
    const newMax = parseFloat(e.target.value);
    onUpdate({
      ...filter,
      values: {
        ...filter.values,
        minSpend: Math.min(currentMinSpend, newMax),
        maxSpend: newMax
      }
    });
  };

  return (
    <div className="filter-container">
      <div className="filter-header">
        <h3>Spend Per Person</h3>
        <button className="filter-remove-btn" onClick={onRemove}>×</button>
      </div>

      <div className="filter-section">
        <div className="filter-subgroup">
          <label>
            <strong>Category:</strong>
            <select
              value={selectedCategory || ''}
              onChange={handleCategoryChange}
              className="filter-select"
              style={{ width: '100%', marginTop: '0.5rem', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="">Select a category...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </label>
        </div>

        {selectedCategory && (
          <>
            <div className="range-display" style={{ marginTop: '1rem' }}>
              <strong>Spend Range:</strong> £{currentMinSpend.toFixed(2)} to £{currentMaxSpend.toFixed(2)}
            </div>

            <div className="dual-range-control">
              <div className="dual-range-wrapper">
                <input
                  type="range"
                  min={range.min}
                  max={range.max}
                  step="0.01"
                  value={currentMinSpend}
                  onChange={handleMinChange}
                  className="range-slider range-slider-min"
                />
                <input
                  type="range"
                  min={range.min}
                  max={range.max}
                  step="0.01"
                  value={currentMaxSpend}
                  onChange={handleMaxChange}
                  className="range-slider range-slider-max"
                />
              </div>
              <div className="dual-range-labels">
                <span className="range-label">£{currentMinSpend.toFixed(2)}</span>
                <span className="range-label">£{currentMaxSpend.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.5rem', fontStyle: 'italic' }}>
              Filter by {selectedCategory} spend per person in each postcode sector
            </div>
          </>
        )}

        {!selectedCategory && (
          <div style={{ fontSize: '0.85rem', color: '#95a5a6', marginTop: '0.5rem', fontStyle: 'italic' }}>
            Select a spend category to enable filtering
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendPerPersonFilter;
